import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  Users, BookOpen, MessageSquare, Target, TrendingUp, Award, Zap,
  ArrowRight, Check, Clock, Star, Lightbulb, Search, Cpu, Compass,
  Crown, ChevronRight, Heart, Send, User
} from 'lucide-react';
import AppShell from '../components/AppShell';
import FoundersWelcome from '../components/FoundersWelcome';
import SiteTour from '../components/SiteTour';
import ParticleField from '../components/ParticleField';
import PowerScore from '../components/PowerScore';
import ProactiveInsight from '../components/ProactiveInsight';
import { StaggerContainer, FadeUp } from '../components/Animations';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CIRCLE_ICONS = { cpu: Cpu, compass: Compass, 'trending-up': TrendingUp, star: Star, rocket: Target };
const CIRCLE_COLORS = {
  purple: { bg: 'from-purple-500/20 to-purple-900/20', border: 'border-purple-500/30', icon: 'text-purple-400' },
  pink: { bg: 'from-pink-500/20 to-pink-900/20', border: 'border-pink-500/30', icon: 'text-pink-400' },
  amber: { bg: 'from-amber-500/20 to-amber-900/20', border: 'border-amber-500/30', icon: 'text-amber-400' },
  emerald: { bg: 'from-emerald-500/20 to-emerald-900/20', border: 'border-emerald-500/30', icon: 'text-emerald-400' },
  blue: { bg: 'from-blue-500/20 to-blue-900/20', border: 'border-blue-500/30', icon: 'text-blue-400' },
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showWelcome, setShowWelcome] = useState(false);
  const [showTour, setShowTour] = useState(false);
  const [circles, setCircles] = useState([]);
  const [joiningCircle, setJoiningCircle] = useState(null);
  const [powerScore, setPowerScore] = useState(null);
  const [insight, setInsight] = useState(null);
  const [activeTab, setActiveTab] = useState('feed');
  const [posts, setPosts] = useState([]);
  const [members, setMembers] = useState([]);
  const [newPost, setNewPost] = useState('');

  const user = JSON.parse(localStorage.getItem('pivot_user') || '{}');

  useEffect(() => {
    loadDashboard(); loadCircles(); loadPowerScore(); loadInsight(); loadPosts(); loadMembers();
  }, []);

  const loadDashboard = async () => {
    try {
      const res = await axios.get(`${API}/dashboard/${user.user_id}`);
      setDashboard(res.data);
      if (!localStorage.getItem('pivot_welcomed')) setShowWelcome(true);
    } catch (e) { console.error('Dashboard load failed', e); }
    finally { setLoading(false); }
  };
  const loadCircles = async () => { try { const r = await axios.get(`${API}/circles/list?user_id=${user.user_id}`); setCircles(r.data); } catch(e){} };
  const loadPowerScore = async () => { try { const r = await axios.get(`${API}/power-score/${user.user_id}`); setPowerScore(r.data); } catch(e){} };
  const loadInsight = async () => { try { const r = await axios.get(`${API}/ai/proactive-insight/${user.user_id}`); setInsight(r.data.insight); } catch(e){} };
  const loadPosts = async () => { try { const r = await axios.get(`${API}/community/posts?limit=10`); setPosts(r.data); } catch(e){} };
  const loadMembers = async () => { try { const r = await axios.get(`${API}/members/directory`); setMembers(r.data); } catch(e){} };

  const handleJoinCircle = async (circleId) => {
    setJoiningCircle(circleId);
    try {
      const circle = circles.find(c => c.circle_id === circleId);
      if (circle?.is_member) {
        await axios.post(`${API}/circles/${circleId}/leave`, { user_id: user.user_id });
        toast.success('Left circle');
      } else {
        await axios.post(`${API}/circles/${circleId}/join`, { user_id: user.user_id });
        toast.success('Joined circle!');
      }
      loadCircles(); loadPowerScore();
    } catch(e) { toast.error('Failed'); }
    finally { setJoiningCircle(null); }
  };

  const handlePost = async (e) => {
    e.preventDefault();
    if (!newPost.trim()) return;
    try {
      await axios.post(`${API}/community/posts`, { user_id: user.user_id, content: newPost });
      setNewPost(''); toast.success('Posted!'); loadPosts();
    } catch(e) { toast.error('Failed to post'); }
  };

  const handleLike = async (postId) => {
    try {
      const r = await axios.post(`${API}/community/posts/${postId}/like`, { user_id: user.user_id });
      setPosts(prev => prev.map(p => p.post_id === postId ? {...p, likes: r.data.likes, liked_by: r.data.liked_by} : p));
    } catch(e) {}
  };

  const handleCloseWelcome = () => {
    setShowWelcome(false);
    localStorage.setItem('pivot_welcomed', 'true');
    if (!localStorage.getItem('pivot_toured')) setTimeout(() => setShowTour(true), 500);
  };
  const handleCompleteTour = () => { setShowTour(false); localStorage.setItem('pivot_toured', 'true'); };

  if (loading) {
    return (
      <AppShell>
        <div className="flex items-center justify-center py-20">
          <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
            className="w-10 h-10 border-2 border-purple-500/30 border-t-purple-400 rounded-full" />
        </div>
      </AppShell>
    );
  }

  const stages = ['assess', 'architect', 'automate', 'accelerate'];
  const stageLabels = ['Assess', 'Architect', 'Automate', 'Accelerate'];
  const stageColors = ['text-purple-400', 'text-pink-400', 'text-amber-400', 'text-emerald-400'];
  const completedStages = stages.filter(s => dashboard?.pivot_journey?.stages?.[s]).length;
  const progressPct = dashboard?.pivot_journey ? (completedStages / 4) * 100 : 0;

  const tabs = [
    { key: 'feed', label: 'Feed', icon: MessageSquare },
    { key: 'members', label: 'Members', icon: Users },
    { key: 'power-moves', label: 'Power Moves', icon: Zap },
  ];

  return (
    <AppShell>
      <ParticleField />
      <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-6 relative z-10">

        {/* TOP BAR: Welcome + Member Status */}
        <FadeUp>
          <div className="flex items-start justify-between mb-6" data-testid="dashboard-top-bar">
            <div>
              <p className="text-[#D4AF37] text-xs font-inter uppercase tracking-[0.2em] mb-1 font-semibold">The Power Center</p>
              <h1 className="text-3xl sm:text-4xl font-cormorant font-light tracking-tight text-white" data-testid="dashboard-welcome">
                Welcome back, <span className="font-medium">{dashboard?.user?.full_name}</span>
              </h1>
              {dashboard?.archetype && (
                <div className="flex items-center space-x-2 mt-2">
                  <Award className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
                  <span className="text-[#D4AF37] text-sm font-inter font-semibold">{dashboard.archetype.archetype}</span>
                </div>
              )}
            </div>
            <div className="bg-white/[0.07] backdrop-blur-xl rounded-2xl border border-white/[0.08] px-5 py-3" data-testid="member-status">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-[#D4AF37]/20 to-amber-500/10 rounded-full flex items-center justify-center border border-[#D4AF37]/30">
                  <Crown className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.5} />
                </div>
                <div className="text-left">
                  <div className="text-white font-cormorant font-semibold text-sm">Power Member</div>
                  <div className="flex items-center space-x-1.5 text-emerald-400">
                    <motion.div animate={{ scale: [1, 1.3, 1] }} transition={{ duration: 2, repeat: Infinity }}
                      className="w-1.5 h-1.5 bg-emerald-400 rounded-full" />
                    <span className="font-inter text-xs font-medium">Active</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </FadeUp>

        {/* Proactive AI Insight */}
        <ProactiveInsight insight={insight} onDismiss={() => setInsight(null)}
          onAction={(path) => path && navigate(path)} />

        {/* Onboarding CTA if not done */}
        {!dashboard?.archetype && (
          <FadeUp delay={0.1}>
            <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-2xl p-6 mb-6 text-center" data-testid="onboarding-prompt">
              <Award className="w-8 h-8 text-purple-400 mx-auto mb-2" strokeWidth={1.5} />
              <h2 className="text-xl font-cormorant font-medium text-white mb-1">Discover Your Power Path</h2>
              <p className="text-[#A39BB0] font-inter text-sm mb-4">Complete the diagnostic to unlock your personalized roadmap</p>
              <motion.button onClick={() => navigate('/onboarding')} whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                className="bg-gradient-to-r from-pink-500 to-purple-600 text-white font-inter font-medium px-6 py-2.5 rounded-full text-sm"
                data-testid="start-onboarding-button">
                Start Power Path Finder <ArrowRight className="w-4 h-4 inline ml-1" />
              </motion.button>
            </div>
          </FadeUp>
        )}

        {/* Pivot Journey Prompt — show if onboarding done but no journey */}
        {dashboard?.archetype && !dashboard?.pivot_journey && (
          <FadeUp delay={0.15}>
            <div className="bg-gradient-to-r from-amber-500/10 to-pink-500/10 border border-amber-500/25 rounded-2xl p-6 mb-6" data-testid="journey-prompt">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-pink-500 rounded-xl flex items-center justify-center shadow-lg">
                    <Target className="w-7 h-7 text-white" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h2 className="text-xl font-cormorant font-bold text-white mb-1">Your Pivot Journey Awaits</h2>
                    <p className="text-[#A39BB0] font-inter text-sm max-w-md">This is the foundation of everything we build for you. The 4-stage assessment takes 15 minutes and unlocks your personalized Pivot Blueprint.</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3 flex-shrink-0 ml-4">
                  <motion.button onClick={() => navigate('/lifestyle-designer')} whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                    className="bg-gradient-to-r from-amber-500 to-pink-500 text-black font-inter font-bold px-6 py-2.5 rounded-full text-sm shadow-lg shadow-amber-500/20"
                    data-testid="start-journey-button">
                    Start Now <ArrowRight className="w-4 h-4 inline ml-1" />
                  </motion.button>
                </div>
              </div>
            </div>
          </FadeUp>
        )}

        {/* MAIN GRID */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

          {/* LEFT / MAIN COLUMN */}
          <div className="lg:col-span-8 space-y-6">

            {/* Tab Bar */}
            <div className="flex items-center space-x-1 bg-white/[0.04] rounded-xl p-1 border border-white/[0.06]" data-testid="dashboard-tabs">
              {tabs.map(t => {
                const Icon = t.icon;
                return (
                  <button key={t.key} onClick={() => setActiveTab(t.key)}
                    className={`flex items-center space-x-2 flex-1 justify-center px-4 py-2.5 rounded-lg text-sm font-inter font-medium transition-all ${
                      activeTab === t.key ? 'bg-white/10 text-white shadow-sm' : 'text-[#A39BB0] hover:text-white hover:bg-white/5'
                    }`} data-testid={`tab-${t.key}`}>
                    <Icon className="w-4 h-4" strokeWidth={1.5} />
                    <span>{t.label}</span>
                  </button>
                );
              })}
            </div>

            {/* FEED TAB */}
            {activeTab === 'feed' && (
              <div className="space-y-4">
                <form onSubmit={handlePost}>
                  <div className="bg-white/[0.07] backdrop-blur-xl rounded-2xl border border-white/[0.08] p-4">
                    <div className="flex items-start space-x-3">
                      <div className="w-9 h-9 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-xs font-bold">{user.full_name?.charAt(0)}</span>
                      </div>
                      <textarea value={newPost} onChange={e => setNewPost(e.target.value)}
                        className="flex-1 bg-transparent border-none text-white placeholder:text-[#A39BB0]/50 outline-none min-h-[50px] resize-none font-inter text-sm"
                        placeholder="Share your wins, questions, or insights..." data-testid="dashboard-post-input" />
                    </div>
                    {newPost.trim() && (
                      <div className="flex justify-end pt-3 border-t border-white/[0.06] mt-3">
                        <motion.button type="submit" whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                          className="flex items-center space-x-2 bg-gradient-to-r from-purple-600 to-pink-500 text-white font-inter font-bold px-4 py-2 rounded-full text-xs"
                          data-testid="dashboard-post-submit">
                          <Send className="w-3.5 h-3.5" /><span>Share</span>
                        </motion.button>
                      </div>
                    )}
                  </div>
                </form>

                {posts.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="w-10 h-10 text-white/15 mx-auto mb-3" />
                    <p className="text-[#A39BB0] font-inter text-sm">No posts yet. Be the first!</p>
                  </div>
                ) : (
                  posts.slice(0, 8).map(post => {
                    const isLiked = (post.liked_by || []).includes(user.user_id);
                    return (
                      <motion.div key={post.post_id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                        className="bg-white/[0.07] backdrop-blur-xl rounded-2xl border border-white/[0.08] p-5" data-testid={`feed-post-${post.post_id}`}>
                        <div className="flex items-start space-x-3 mb-3">
                          <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 cursor-pointer"
                            onClick={() => navigate(`/profile/${post.user_id}`)}>
                            {post.author_photo ? (
                              <img src={post.author_photo} alt="" className="w-10 h-10 rounded-full object-cover" />
                            ) : (
                              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                                <User className="w-5 h-5 text-white" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <button onClick={() => navigate(`/profile/${post.user_id}`)}
                              className="text-white font-inter font-medium text-sm hover:text-purple-300 transition-colors">{post.author_name}</button>
                            <p className="text-[#A39BB0]/50 text-xs font-inter">
                              {new Date(post.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                            </p>
                          </div>
                        </div>
                        <p className="text-white/80 font-inter text-sm leading-relaxed whitespace-pre-wrap mb-3">{post.content}</p>
                        <div className="flex items-center space-x-4 pt-3 border-t border-white/[0.06]">
                          <button onClick={() => handleLike(post.post_id)}
                            className={`flex items-center space-x-1.5 text-xs font-inter transition-colors ${isLiked ? 'text-pink-400' : 'text-[#A39BB0] hover:text-pink-400'}`}
                            data-testid={`feed-like-${post.post_id}`}>
                            <Heart className={`w-4 h-4 ${isLiked ? 'fill-pink-400' : ''}`} /><span>{post.likes || 0}</span>
                          </button>
                          <button onClick={() => navigate('/community')}
                            className="flex items-center space-x-1.5 text-[#A39BB0] hover:text-purple-300 text-xs font-inter transition-colors">
                            <MessageSquare className="w-4 h-4" /><span>{(post.comments || []).length} Replies</span>
                          </button>
                        </div>
                      </motion.div>
                    );
                  })
                )}

                {posts.length > 0 && (
                  <button onClick={() => navigate('/community')}
                    className="w-full text-center text-purple-300 hover:text-white text-sm font-inter py-3 bg-white/[0.03] rounded-xl border border-white/[0.06] hover:bg-white/[0.06] transition-all"
                    data-testid="view-all-posts">
                    View All in Community <ArrowRight className="w-3.5 h-3.5 inline ml-1" />
                  </button>
                )}
              </div>
            )}

            {/* MEMBERS TAB */}
            {activeTab === 'members' && (
              <div className="space-y-4">
                <StaggerContainer className="grid md:grid-cols-2 gap-3">
                  {members.filter(m => m.user_id !== user.user_id).slice(0, 12).map((member, i) => (
                    <FadeUp key={member.user_id || i}>
                      <motion.div whileHover={{ y: -2 }}
                        className="bg-white/[0.07] backdrop-blur-xl rounded-xl border border-white/[0.08] p-4 hover:border-white/15 transition-all cursor-pointer"
                        onClick={() => navigate(`/profile/${member.user_id}`)} data-testid={`dashboard-member-${i}`}>
                        <div className="flex items-center space-x-3">
                          {member.photo_url ? (
                            <img src={member.photo_url} alt={member.full_name} className="w-12 h-12 rounded-full object-cover" />
                          ) : (
                            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                              <span className="text-white font-bold text-lg">{member.full_name?.charAt(0)}</span>
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <h3 className="text-white font-inter font-medium text-sm truncate">{member.full_name}</h3>
                            {member.headline && <p className="text-[#A39BB0] text-xs font-inter truncate">{member.headline}</p>}
                            {member.archetype && (
                              <span className="inline-flex items-center space-x-1 text-[10px] text-purple-300 font-inter mt-1">
                                <Award className="w-3 h-3" /><span>{member.archetype}</span>
                              </span>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    </FadeUp>
                  ))}
                </StaggerContainer>
                <button onClick={() => navigate('/community')}
                  className="w-full text-center text-purple-300 hover:text-white text-sm font-inter py-3 bg-white/[0.03] rounded-xl border border-white/[0.06] hover:bg-white/[0.06] transition-all"
                  data-testid="view-all-members">
                  View All Members <ArrowRight className="w-3.5 h-3.5 inline ml-1" />
                </button>
              </div>
            )}

            {/* POWER MOVES TAB */}
            {activeTab === 'power-moves' && (
              <div className="space-y-6">
                {(dashboard?.next_actions || []).length > 0 && (
                  <div>
                    <h2 className="text-xs font-inter font-bold uppercase tracking-[0.2em] text-[#A39BB0] mb-3 flex items-center space-x-2">
                      <Clock className="w-3.5 h-3.5" strokeWidth={1.5} /><span>Your Next Move</span>
                    </h2>
                    <div className="grid sm:grid-cols-3 gap-3">
                      {dashboard.next_actions.map((action, i) => (
                        <motion.div key={i} whileHover={{ y: -3 }}
                          className="bg-white/[0.05] backdrop-blur-xl p-4 rounded-xl border border-white/[0.08] cursor-pointer group">
                          <div className="flex items-start space-x-3">
                            <div className="w-7 h-7 bg-gradient-to-br from-pink-500/30 to-purple-600/30 rounded-lg flex items-center justify-center flex-shrink-0 border border-pink-500/20">
                              <span className="text-pink-400 font-bold text-[10px] font-inter">{String(i + 1).padStart(2, '0')}</span>
                            </div>
                            <p className="text-[#A39BB0] font-inter text-sm leading-relaxed group-hover:text-white transition-colors">{action}</p>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                )}

                {dashboard?.pivot_journey && (
                  <div className="bg-white/[0.07] backdrop-blur-xl rounded-2xl border border-white/[0.08] p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-sm font-inter font-semibold text-white flex items-center space-x-2">
                        <Target className="w-4 h-4 text-pink-400" strokeWidth={1.5} /><span>Pivot Journey</span>
                      </h3>
                      <button onClick={() => navigate('/lifestyle-designer')}
                        className="text-[#A39BB0] hover:text-white text-xs font-inter flex items-center space-x-1" data-testid="continue-journey">
                        <span>{dashboard.pivot_journey.completed_at ? 'View Blueprint' : 'Continue'}</span>
                        <ChevronRight className="w-3 h-3" />
                      </button>
                    </div>
                    <div className="bg-white/5 rounded-full h-2 mb-3 overflow-hidden">
                      <motion.div className="bg-gradient-to-r from-pink-500 to-[#D4AF37] rounded-full h-2"
                        initial={{ width: 0 }} animate={{ width: `${progressPct}%` }}
                        transition={{ duration: 1.2 }} />
                    </div>
                    <div className="grid grid-cols-4 gap-2">
                      {stages.map((stage, i) => {
                        const done = !!dashboard.pivot_journey.stages?.[stage];
                        return (
                          <div key={stage} className={`text-center py-2 px-1 rounded-lg ${done ? 'bg-white/10 border border-white/10' : 'border border-white/5'}`}>
                            <div className={`text-[10px] font-inter font-bold uppercase tracking-wider mb-1 ${done ? stageColors[i] : 'text-[#A39BB0]/50'}`}>{stageLabels[i]}</div>
                            {done ? <Check className="w-3.5 h-3.5 text-emerald-400 mx-auto" strokeWidth={2} /> : <div className="w-3.5 h-3.5 rounded-full border border-white/20 mx-auto" />}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div>
                  <h2 className="text-xs font-inter font-bold uppercase tracking-[0.2em] text-[#A39BB0] mb-3 flex items-center space-x-2">
                    <Zap className="w-3.5 h-3.5" strokeWidth={1.5} /><span>Power Tools</span>
                  </h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {[
                      { title: 'Power Guide', desc: 'Strategic AI advisor', icon: MessageSquare, gradient: 'from-pink-500 to-purple-600', path: '/ai-guide', testId: 'power-tool-ai-guide' },
                      { title: 'Pivot Journey', desc: 'Assess, Architect, Automate, Accelerate', icon: Target, gradient: 'from-purple-500 to-indigo-600', path: '/lifestyle-designer', testId: 'power-tool-journey' },
                      { title: 'Profile Analyzer', desc: 'Power score & roadmap', icon: Search, gradient: 'from-blue-500 to-purple-600', path: '/profile-analyzer', testId: 'power-tool-analyzer' },
                      { title: 'Community', desc: 'Connect with members', icon: Users, gradient: 'from-fuchsia-500 to-purple-600', path: '/community', testId: 'power-tool-community' },
                      { title: 'Power Vault', desc: 'Exclusive insights', icon: BookOpen, gradient: 'from-emerald-500 to-teal-600', path: '/power-vault', testId: 'power-tool-vault' },
                    ].map((action, i) => {
                      const Icon = action.icon;
                      return (
                        <motion.button key={i} onClick={() => navigate(action.path)}
                          whileHover={{ y: -4, scale: 1.02 }} whileTap={{ scale: 0.97 }}
                          className="w-full bg-white/[0.05] backdrop-blur-xl p-5 rounded-2xl border border-white/[0.08] hover:border-white/15 text-left"
                          data-testid={action.testId}>
                          <div className={`w-10 h-10 bg-gradient-to-br ${action.gradient} rounded-xl flex items-center justify-center mb-3`}>
                            <Icon className="w-5 h-5 text-white" strokeWidth={1.5} />
                          </div>
                          <h3 className="text-base font-cormorant font-semibold text-white mb-0.5">{action.title}</h3>
                          <p className="text-[#A39BB0] text-[11px] font-inter">{action.desc}</p>
                        </motion.button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* RIGHT SIDEBAR */}
          <div className="lg:col-span-4 space-y-6">

            {/* Idea Lab CTA - Prominent */}
            <FadeUp>
              <motion.div whileHover={{ scale: 1.01 }}
                className="relative overflow-hidden bg-gradient-to-br from-amber-500/10 to-pink-500/10 backdrop-blur-xl rounded-2xl border border-amber-500/30 p-6 cursor-pointer"
                onClick={() => navigate('/idea-lab')} data-testid="idea-lab-cta">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-11 h-11 bg-gradient-to-br from-amber-500 to-pink-500 rounded-xl flex items-center justify-center shadow-lg">
                    <Lightbulb className="w-5 h-5 text-white" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="text-lg font-cormorant font-bold text-white">Power Lab</h3>
                    <p className="text-amber-300/80 text-xs font-inter">Turn ideas into action plans</p>
                  </div>
                </div>
                <p className="text-[#A39BB0] font-inter text-xs leading-relaxed mb-4">
                  Describe your idea and get a complete build guide with AI tools, prompts, and step-by-step instructions.
                </p>
                <div className="w-full bg-gradient-to-r from-amber-500 to-pink-500 text-white font-inter font-bold text-sm py-2.5 rounded-full text-center shadow-[0_0_15px_rgba(245,158,11,0.3)]">
                  Launch Power Lab <ArrowRight className="w-4 h-4 inline ml-1" />
                </div>
              </motion.div>
            </FadeUp>

            {/* Power Circles - Elevated */}
            <FadeUp delay={0.1}>
              <div className="bg-white/[0.07] backdrop-blur-xl rounded-2xl border border-white/[0.08] p-5" data-testid="power-circles-section">
                <h3 className="text-xs font-inter font-bold uppercase tracking-[0.2em] text-[#A39BB0] mb-4 flex items-center space-x-2">
                  <Users className="w-3.5 h-3.5" strokeWidth={1.5} /><span>Power Circles</span>
                </h3>
                <div className="space-y-2">
                  {circles.map(circle => {
                    const cc = CIRCLE_COLORS[circle.color] || CIRCLE_COLORS.purple;
                    const CircleIcon = CIRCLE_ICONS[circle.icon] || Star;
                    return (
                      <motion.div key={circle.circle_id} whileHover={{ x: 3 }}
                        className={`bg-gradient-to-r ${cc.bg} rounded-xl border ${cc.border} p-3 cursor-pointer`}
                        onClick={() => circle.is_member ? navigate(`/circle/${circle.circle_id}`) : handleJoinCircle(circle.circle_id)}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2.5 flex-1 min-w-0">
                            <CircleIcon className={`w-4 h-4 ${cc.icon} flex-shrink-0`} strokeWidth={1.5} />
                            <div className="min-w-0">
                              <h4 className="text-white font-inter text-xs font-semibold truncate">{circle.name}</h4>
                              <p className="text-[#A39BB0] text-[10px] font-inter">{circle.members} members</p>
                            </div>
                          </div>
                          {circle.is_member ? (
                            <span className="flex-shrink-0 text-[10px] font-inter font-bold px-3 py-1 rounded-full bg-white/10 text-emerald-400">Enter</span>
                          ) : (
                            <motion.button onClick={(e) => { e.stopPropagation(); handleJoinCircle(circle.circle_id); }}
                              whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                              disabled={joiningCircle === circle.circle_id}
                              className="flex-shrink-0 text-[10px] font-inter font-bold px-3 py-1 rounded-full bg-gradient-to-r from-pink-500 to-purple-600 text-white"
                              data-testid={`circle-join-${circle.circle_id}`}>
                              {joiningCircle === circle.circle_id ? '...' : 'Join'}
                            </motion.button>
                          )}
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            </FadeUp>

            {/* Power Score - Moved Down */}
            <FadeUp delay={0.2}>
              <div className="bg-white/[0.07] backdrop-blur-xl rounded-2xl border border-white/[0.08] p-6 text-center" data-testid="power-score-card">
                <div className="text-[#A39BB0] text-[10px] font-inter uppercase tracking-[0.2em] mb-4">Your Power Score</div>
                <PowerScore score={powerScore?.score || 0} size={120} />
                <div className="mt-3 text-white font-cormorant font-semibold text-lg">{powerScore?.level || 'Starter'}</div>
                {powerScore?.breakdown && (
                  <div className="mt-4 grid grid-cols-3 gap-2">
                    {[
                      { label: 'Journey', value: powerScore.breakdown.pivot_journey || 0, max: 40 },
                      { label: 'Community', value: (powerScore.breakdown.community || 0) + (powerScore.breakdown.circles || 0), max: 22 },
                      { label: 'AI Tools', value: powerScore.breakdown.ai_tools || 0, max: 15 },
                    ].map((b, i) => (
                      <div key={i} className="bg-white/5 rounded-lg p-2">
                        <div className="text-[#A39BB0] text-[9px] font-inter uppercase tracking-wider">{b.label}</div>
                        <div className="text-white font-inter font-semibold text-sm">{b.value}<span className="text-white/30 text-[10px]">/{b.max}</span></div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </FadeUp>
          </div>
        </div>
      </div>

      {showWelcome && <FoundersWelcome onClose={handleCloseWelcome} />}
      {showTour && <SiteTour onComplete={handleCompleteTour} />}
    </AppShell>
  );
}
